import { useEffect, useState, useContext, useMemo } from "react";
import { createPortal } from "react-dom";
import { useEffectOnce } from "../../hooks/useEffectOnce";
import { useNavigate, useLocation } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import useRetryFetch from "../../hooks/useRetryFetch";
import { useTranslation } from "react-i18next";
import { config } from "../../Environment";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  solid /* regular */,
} from "@fortawesome/fontawesome-svg-core/import.macro"; // <-- import styles to be used

import LoggerContext from "../../context/LoggerProvider";
import { toast } from "sonner";
import { LocationState, User } from "../../lib/types";
import Activities from "./activities";
import Mentoring from "./mentoring";
import Bundle from "./bundle";
import Services from "./services";
import Platform from "./platform";
import PwfProducts from "./pwfs";
import ProgramProducts from "./programs";
import CategorySidebar from "./CategorySidebar";
import GlobalFilterPopover from "./GlobalFilterPopover";
import { SYSADMIN_ROLE } from "../../lib/roles";
import { WelcomeModal, checkShouldShowWelcomeModal } from "./WelcomeModal";
import { HeroSection } from "./HeroSection";
import { Button } from "../ui/button";
import {
  Menu,
  X,
  ListVideo,
  Briefcase,
  Package,
  Layers,
  Search,
  Filter,
  RotateCcw,
  MonitorPlay,
  BriefcaseBusiness,
  Waypoints,
  LayoutList
} from "lucide-react";
import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Badge } from "../ui/badge";
import { useCompanion } from "../../context/CompanionProvider";
import { cn } from "../../lib/utils";
import { useMarketplaceFilters } from "../../hooks/useMarketplaceFilters";
import { useProductSearch, enrichVendorLogos } from "../../hooks/useProductSearch";
import { useZoneSidebar } from "../../context/ZoneSidebarContext";
import { useRagProductSearch } from "../../hooks/useRagProductSearch";
import { searchIdentity } from "../../api/rag";

const Marketplace = () => {
  const { user, auth } = useAuth();
  const { portalContainer, collapsed: zoneSidebarCollapsed } = useZoneSidebar();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  // Try URL param first, then localStorage, then default to 'activities'
  const selectedTab = queryParams.get('tab') ?? localStorage.getItem('marketplace_active_tab') ?? 'activities';
  const navigate = useNavigate();

  const { t } = useTranslation();

  const [isMobile, setIsMobile] = useState(
    window.matchMedia("(max-width: 800px)").matches
  );

  const roles = user.roles ?? [];
  const isSysadmin = roles.includes(SYSADMIN_ROLE);

  // Check if user is authenticated
  const isAuthenticated = !!auth?.email;

  const [errMsg, setErrMsg] = useState("");
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [globalSearch, setGlobalSearch] = useState("");
  const [globalFilterOpen, setGlobalFilterOpen] = useState(false);
  const [isAiSearch, setIsAiSearch] = useState(false);
  const [ragResults, setRagResults] = useState<any[] | null>(null);
  const [ragLoading, setRagLoading] = useState(false);

  // Global filter state (shared across all tabs)
  const [globalFilters, setGlobalFilters] = useState({
    search: "",
    categories: [] as string[],
    tags: [] as string[],
    difficulty: [] as string[],
    estimatedTimeMin: undefined as number | undefined,
    estimatedTimeMax: undefined as number | undefined,
    priceMin: undefined as number | undefined,
    priceMax: undefined as number | undefined,
    vendorId: undefined as string | undefined,
  });

  // Filter options (loaded once, cached via React Query)
  const { availableCategories, availableTags, availableVendors } = useMarketplaceFilters();

  // RAG search
  const ragSearch = useRagProductSearch();

  async function handleRagSearch(queryOverride?: string) {
    const query = (queryOverride ?? globalSearch).trim();
    if (!query) {
      toast.error(t("ai_search_empty") || "Please enter a search query");
      return;
    }
    setRagLoading(true);
    try {
      const response = await ragSearch.mutateAsync({
        query,
        filters: {
          categories: globalFilters.categories.length > 0 ? globalFilters.categories : undefined,
          tags: globalFilters.tags.length > 0 ? globalFilters.tags : undefined,
          vendorId: globalFilters.vendorId,
        },
        limit: 20,
        includeExplanations: true,
      });
      const enriched = await enrichVendorLogos(response.results || [], retryFetch);
      // Attach per-result AI explanations ("why this matches") for the cards
      const explanations = response.explanations || {};
      for (const product of enriched) {
        const explanation = explanations[product._id];
        // The AI answers "Not directly related..." for weak matches — skip those
        if (explanation && !/^not directly related/i.test(explanation)) {
          product.aiExplanation = explanation;
        }
      }
      setRagResults(enriched);
    } catch (err: any) {
      toast.error(err?.message || t("ai_search_failed") || "AI search failed");
      setRagResults(null);
    } finally {
      setRagLoading(false);
    }
  }

  // Trait-focused search: the backend reformulates the trait into a semantic
  // query (POST /api/rag/search/identity) instead of searching the raw name.
  async function handleTraitSearch(
    spaceId: string,
    traitId: string,
    fallbackQuery: string
  ) {
    setRagLoading(true);
    try {
      const response = await searchIdentity(retryFetch, {
        identitySpaceId: spaceId,
        traitId,
        limit: 20,
      });
      const enriched = await enrichVendorLogos(response.products || [], retryFetch);
      setRagResults(enriched);
    } catch (err: any) {
      if (fallbackQuery) {
        // Trait search unavailable (e.g. trait no longer in space) — fall back
        // to a plain AI search on the trait name.
        await handleRagSearch(fallbackQuery);
      } else {
        toast.error(err?.message || t("ai_search_failed") || "AI search failed");
        setRagResults(null);
      }
    } finally {
      setRagLoading(false);
    }
  }

  // Seed the AI search from URL params — used by deep links such as the
  // "Find activities for this trait" button (?q=<name>&traitId=..&spaceId=..).
  useEffectOnce(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get("q");
    const traitId = params.get("traitId");
    const spaceId = params.get("spaceId");
    if (!q && !traitId) return;

    if (q) setGlobalSearch(q);
    setIsAiSearch(true);
    if (traitId && spaceId) {
      handleTraitSearch(spaceId, traitId, q || "");
    } else if (q) {
      handleRagSearch(q);
    }
  }, []);

  function handleAiSearchToggle(enabled: boolean) {
    setIsAiSearch(enabled);
    if (!enabled) {
      setRagResults(null);
      setRagLoading(false);
    }
  }

  // Product counts per tab (reactive to filters)
  const { data: activityProducts = [] } = useProductSearch({ type: "activity", filters: globalFilters });
  const { data: serviceProducts = [] } = useProductSearch({ type: "service", filters: globalFilters });
  const { data: bundleProducts = [] } = useProductSearch({ type: "bundle", filters: globalFilters });
  const { data: pwfProducts = [] } = useProductSearch({ type: "pwf", filters: globalFilters });
  const { data: programProducts = [] } = useProductSearch({ type: "program", filters: globalFilters });

  // Unfiltered counts for sidebar "All Products" total (ignores category filter)
  const filtersWithoutCategory = useMemo(() => ({ ...globalFilters, categories: [] as string[] }), [globalFilters]);
  const { data: allActivities = [] } = useProductSearch({ type: "activity", filters: filtersWithoutCategory });
  const { data: allServices = [] } = useProductSearch({ type: "service", filters: filtersWithoutCategory });
  const { data: allBundles = [] } = useProductSearch({ type: "bundle", filters: filtersWithoutCategory });
  const { data: allPwfs = [] } = useProductSearch({ type: "pwf", filters: filtersWithoutCategory });
  const { data: allPrograms = [] } = useProductSearch({ type: "program", filters: filtersWithoutCategory });
  const sidebarTotal = allActivities.length + allServices.length + allBundles.length + allPwfs.length + allPrograms.length;

  const tabCounts: Record<string, number> = useMemo(() => ({
    activities: activityProducts.length,
    services: serviceProducts.length,
    bundle: bundleProducts.length,
    pwfs: pwfProducts.length,
    programs: programProducts.length,
  }), [activityProducts.length, serviceProducts.length, bundleProducts.length, pwfProducts.length, programProducts.length]);

  const marketplaceComponents = [
    {
      id: "activities",
      label: "activity_list",
      icon: MonitorPlay,
      activeColor: "#0d9488",  // teal-600
      activeBg: "#f0fdfa",     // teal-50
      badgeBg: "#ccfbf1",      // teal-100
      badgeText: "#0f766e",    // teal-700
      content: () => <Activities filters={globalFilters} ragResults={ragResults} ragLoading={ragLoading} />,
    },
    {
      id: "pwfs",
      label: "pwf_list",
      icon: Waypoints,
      activeColor: "#0d9488",  // teal-600
      activeBg: "#f0fdfa",     // teal-50
      badgeBg: "#ccfbf1",      // teal-100
      badgeText: "#0f766e",    // teal-700
      content: () => <PwfProducts filters={globalFilters} ragResults={ragResults} ragLoading={ragLoading} />,
    },
    {
      id: "programs",
      label: "program_list",
      icon: LayoutList,
      activeColor: "#0d9488",  // teal-600
      activeBg: "#f0fdfa",     // teal-50
      badgeBg: "#ccfbf1",      // teal-100
      badgeText: "#0f766e",    // teal-700
      content: () => <ProgramProducts filters={globalFilters} ragResults={ragResults} ragLoading={ragLoading} />,
    },
    {
      id: "services",
      label: "services",
      icon: BriefcaseBusiness,
      activeColor: "#059669",  // emerald-600
      activeBg: "#ecfdf5",     // emerald-50
      badgeBg: "#d1fae5",      // emerald-100
      badgeText: "#047857",    // emerald-700
      content: () => <Services filters={globalFilters} ragResults={ragResults} ragLoading={ragLoading} />,
    },
    // {
    //   id: "mentoring",
    //   label: "mentoring",
    //   content: () => <Mentoring />,
    // },
    {
      id: "bundle",
      label: "bundles",
      icon: Package,
      activeColor: "#0d9488",  // teal-600
      activeBg: "#f0fdfa",     // teal-50
      badgeBg: "#ccfbf1",      // teal-100
      badgeText: "#0f766e",    // teal-700
      content: () => <Bundle filters={globalFilters} ragResults={ragResults} ragLoading={ragLoading} />,
    },
    {
      id: "platform",
      label: "platform",
      icon: Layers,
      activeColor: "#0f766e",  // teal-700
      activeBg: "#f0fdfa",     // teal-50
      badgeBg: "#ccfbf1",      // teal-100
      badgeText: "#134e4a",    // teal-900
      content: () => <Platform />,
    },
  ];

  const { Logger } = useContext(LoggerContext);

  const retryFetch = useRetryFetch();

  useEffectOnce(() => {
    const handler = (e: any) => setIsMobile(e.matches);
    window.matchMedia("(max-width: 800px)").addEventListener("change", handler);
  }, []);

  // Filter options are now loaded via useMarketplaceFilters() hook (React Query cached)

  function desktopTabChangeHandler(id: string) {
    return function handler(e: any) {
      // Save to localStorage
      localStorage.setItem('marketplace_active_tab', id);

      // Check if we're inside portal by looking at current pathname
      const isInPortal = location.pathname === '/portal';
      if (isInPortal) {
        // Don't navigate, just let the tab change happen naturally
        return;
      }
      navigate(`/marketplace?tab=${id}`, { replace: true });
    };
  }

  function mobileTabChangeHandler(id: string) {
    return function handler(e: any) {
      // Save to localStorage
      localStorage.setItem('marketplace_active_tab', id);

      // Check if we're inside portal by looking at current pathname
      const isInPortal = location.pathname === '/portal';
      if (isInPortal) {
        // Don't navigate, just let the tab change happen naturally
        return;
      }
      navigate(`/marketplace?tab=${id}`, { replace: true });
    };
  }

  function handleTabChange(tabId: string) {
    // Save to localStorage
    localStorage.setItem('marketplace_active_tab', tabId);

    const isInPortal = location.pathname === '/portal';
    if (!isInPortal) {
      navigate(`/marketplace?tab=${tabId}`, { replace: true });
    }
  }

  function renderDesktopTab(item: typeof marketplaceComponents[0]) {
    const { id, label, icon: Icon, activeColor, activeBg, badgeBg, badgeText } = item;
    const count = tabCounts[id];
    const isActive = selectedTab === id;
    return (
      <TabsTrigger key={id}
        className={cn(
          "group relative inline-flex items-center justify-center gap-2 whitespace-nowrap px-4 py-2 text-sm font-medium rounded-xl transition-all duration-200",
          "disabled:pointer-events-none disabled:opacity-50",
          isActive && "shadow-sm",
        )}
        style={{
          color: isActive ? activeColor : "#64748b",
          backgroundColor: isActive ? "white" : "transparent",
          ...(isActive ? { boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)" } : {}),
        }}
        value={id}
        onClick={desktopTabChangeHandler(id)}
      >
        <Icon className="w-4 h-4" />
        <span>{t(label)}</span>
        {count !== undefined && count > 0 && (
          <span
            className="h-5 min-w-[20px] px-1.5 text-[10px] font-bold rounded-full inline-flex items-center justify-center transition-colors"
            style={{
              backgroundColor: isActive ? `${activeColor}14` : "#f1f5f9",
              color: isActive ? activeColor : "#94a3b8",
            }}
          >
            {count}
          </span>
        )}
      </TabsTrigger>
    );
  }

  function renderDesktopTabsList() {
    const tablist = [];
    for (let i = 0; i < marketplaceComponents.length; i += 1) {
      const item = marketplaceComponents[i];
      tablist.push(renderDesktopTab(item));
    }

    // Calculate active filter count
    const activeFilterCount =
      globalFilters.categories.length +
      globalFilters.tags.length +
      globalFilters.difficulty.length +
      (globalFilters.vendorId ? 1 : 0) +
      (globalFilters.estimatedTimeMin !== undefined || globalFilters.estimatedTimeMax !== undefined ? 1 : 0) +
      (globalFilters.priceMin !== undefined || globalFilters.priceMax !== undefined ? 1 : 0);

    return (
      <div className="flex items-center justify-between gap-4 px-5 py-2.5">
        <TabsList className="inline-flex h-auto items-center justify-start gap-1 p-1 rounded-2xl bg-slate-100/80">
          {tablist}
        </TabsList>
        {/* Quick Actions on the right */}
        <div className="flex items-center gap-2">
          {!isAiSearch && (
            <Popover open={globalFilterOpen} onOpenChange={setGlobalFilterOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className={`h-8 gap-1.5 rounded-lg text-xs font-medium border transition-all duration-150 ${
                    activeFilterCount > 0
                      ? "border-primary/40 bg-primary/5 text-primary hover:bg-primary/10"
                      : "border-gray-200/80 text-gray-600 hover:border-primary/30 hover:bg-gray-50 hover:text-primary"
                  }`}
                >
                  <Filter className="h-3.5 w-3.5" />
                  <span>{t("filters") || "Filters"}</span>
                  {activeFilterCount > 0 && (
                    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-primary text-white text-[9px] font-bold ms-0.5">
                      {activeFilterCount}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[90vw] sm:w-[600px] md:w-[700px] p-0 max-h-[70vh] flex flex-col" align="end">
                <GlobalFilterPopover
                  filters={globalFilters}
                  onFilterChange={setGlobalFilters}
                  availableCategories={availableCategories}
                  availableTags={availableTags}
                  availableVendors={availableVendors}
                />
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>
    );
  }

  function renderDesktopTabsContent() {
    const list = [];
    for (let i = 0; i < marketplaceComponents.length; i += 1) {
      const item = marketplaceComponents[i];
      list.push(
        <TabsContent key={item.id} value={item.id} className="mt-0">
          {selectedTab === item.id && typeof item.content === "function"
            ? item.content()
            : null}
        </TabsContent>
      );
    }
    return <>{list}</>;
  }

  const { messages: companionMessages, dismissMessage: companionDismiss } = useCompanion();

  // Hide companion banners while first-time onboarding modal is active
  const showCompanionBanners = !checkShouldShowWelcomeModal();

  return (
    <div className="flex flex-col h-full">
      {/* Welcome Modal */}
      <WelcomeModal
        isAuthenticated={isAuthenticated}
        onClose={() => setShowWelcomeModal(false)}
      />

      {errMsg ? <div className="error">{errMsg}</div> : ""}

      {isMobile ? (
        <div className="flex flex-col h-full">
          {/* Hero Section with embedded companion tip */}
          <HeroSection
            isAuthenticated={isAuthenticated}
            onTabChange={handleTabChange}
            companionMessages={showCompanionBanners ? companionMessages : undefined}
            onDismissCompanion={companionDismiss}
            searchValue={globalSearch}
            onSearchChange={(value) => {
              setGlobalSearch(value);
              if (!isAiSearch) {
                setGlobalFilters({ ...globalFilters, search: value });
              }
            }}
            isAiSearch={isAiSearch}
            onAiSearchToggle={handleAiSearchToggle}
            onSearchSubmit={handleRagSearch}
          />

          <Tabs className="flex flex-col flex-1" value={selectedTab} onValueChange={handleTabChange}>
            {/* Mobile tabs header - sticky below main header */}
            <div className="sticky z-40 bg-white/90 backdrop-blur-md border-b border-gray-200/60"
              style={{ boxShadow: "0 1px 0 0 rgba(0,0,0,0.05), 0 2px 6px -2px rgba(0,0,0,0.04)" }}>
              <div className="overflow-x-auto scrollbar-hide">
                <TabsList className="inline-flex w-full h-auto items-center justify-start bg-transparent p-2 gap-1">
                  {marketplaceComponents.map((item) => {
                    const Icon = item.icon;
                    const count = tabCounts[item.id];
                    const isActive = selectedTab === item.id;
                    return (
                      <TabsTrigger
                        key={item.id}
                        className="group relative whitespace-nowrap px-3 py-2.5 text-xs sm:text-sm font-medium transition-all flex items-center gap-1.5 sm:gap-2 border-b-2"
                        style={{
                          color: isActive ? item.activeColor : "#6b7280",
                          backgroundColor: "transparent",
                          borderBottomColor: isActive ? item.activeColor : "transparent",
                        }}
                        value={item.id}
                        onClick={mobileTabChangeHandler(item.id)}
                      >
                        <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                        <span className="text-[11px] sm:text-sm">{t(item.label)}</span>
                        {count !== undefined && count > 0 && (
                          <span
                            className="h-4 min-w-[16px] px-1 text-[9px] font-medium rounded-full inline-flex items-center justify-center"
                            style={{
                              backgroundColor: isActive ? `${item.activeColor}15` : "#f3f4f6",
                              color: isActive ? item.badgeText : "#6b7280",
                            }}
                          >
                            {count}
                          </span>
                        )}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
              </div>
            </div>

            {/* Tabs content — only render active tab to avoid duplicate API calls */}
            <div className="flex-1 overflow-y-auto">
              {marketplaceComponents.map((item) => (
                <TabsContent key={item.id} className="h-full m-0 p-4" value={item.id}>
                  {selectedTab === item.id && typeof item.content === "function"
                    ? item.content()
                    : null}
                </TabsContent>
              ))}
            </div>
          </Tabs>
        </div>
      ) : (
        <>
          {/* Portal CategorySidebar into ZoneSidebar */}
          {portalContainer && !isMobile && createPortal(
            <CategorySidebar
              selectedCategory={globalFilters.categories[0]}
              onCategorySelect={(categoryId) => {
                if (categoryId) {
                  setGlobalFilters({ ...globalFilters, categories: [categoryId] });
                } else {
                  setGlobalFilters({ ...globalFilters, categories: [] });
                }
              }}
              collapsed={zoneSidebarCollapsed}
              totalProductsOverride={sidebarTotal}
            />,
            portalContainer,
          )}

          <div className="flex h-full overflow-hidden">
            {/* Main Content */}
            <main className="flex-1 flex flex-col overflow-y-auto scrollbar-thin bg-[#fafbfc]">
              {/* Hero */}
              <HeroSection
                isAuthenticated={isAuthenticated}
                onTabChange={handleTabChange}
                companionMessages={showCompanionBanners ? companionMessages : undefined}
                onDismissCompanion={companionDismiss}
                searchValue={globalSearch}
                onSearchChange={(value) => {
                  setGlobalSearch(value);
                  if (!isAiSearch) {
                    setGlobalFilters({ ...globalFilters, search: value });
                  }
                }}
                isAiSearch={isAiSearch}
                onAiSearchToggle={handleAiSearchToggle}
                onSearchSubmit={handleRagSearch}
              />

              {/* Content */}
              <div className="flex-1">
                <Tabs value={selectedTab} onValueChange={handleTabChange}>
                  {/* Sticky tab bar */}
                  <div className="sticky top-0 z-30 border-b border-slate-200/50"
                    style={{ background: "rgba(250,251,252,0.92)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)" }}>
                    {renderDesktopTabsList()}
                  </div>
                  <div className="px-6 pt-5 pb-10">
                    {renderDesktopTabsContent()}
                  </div>
                </Tabs>
              </div>
            </main>
          </div>
        </>
      )}
    </div>
  );
};

export default Marketplace;
